import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI, seedAPI, alertsAPI } from '../../lib/api';
import { Users, CreditCard, TrendingUp, CalendarCheck, Bell, ChevronRight, RefreshCw, Trophy } from 'lucide-react';

const ConfettiOverlay = () => {
  const colors = ['#6db025', '#00C2FF', '#FFD700', '#FF6B6B', '#C084FC', '#34D399'];
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i, color: colors[i % colors.length], left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`, duration: `${2 + Math.random() * 3}s`, size: 4 + Math.random() * 8,
  }));
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <style>{`@keyframes confetti-d { 0% { transform: translateY(-10px) rotate(0deg); opacity:1; } 100% { transform: translateY(100vh) rotate(720deg); opacity:0; } }`}</style>
      {pieces.map(p => <div key={p.id} style={{ position:'absolute', left:p.left, top:'-10px', width:`${p.size}px`, height:`${p.size*0.6}px`, backgroundColor:p.color, animation:`confetti-d ${p.duration} ${p.delay} ease-out forwards` }} />)}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, trend }) => (
  <div className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group">
    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />
    <div className="p-6 pl-7">
      <div className="flex items-start justify-between">
        <div className="w-14 h-14 flex items-center justify-center transition-colors bg-[#6db025]/10 group-hover:bg-[#6db025] text-[#6db025] group-hover:text-black rounded">
          <Icon size={24} />
        </div>
        {trend && <span className={`text-xs font-black uppercase tracking-widest ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>{trend > 0 ? '+' : ''}{trend}%</span>}
      </div>
      <div className="mt-6">
        <p className="text-white/40 text-xs uppercase font-black tracking-widest mb-2">{label}</p>
        <p className="font-heading text-4xl font-bold text-white tracking-tight">{value}</p>
      </div>
    </div>
  </div>
);

const QuickAction = ({ icon: Icon, label, to }) => (
  <Link to={to} className="relative flex items-center gap-4 bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group">
    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />
    <div className="flex items-center gap-4 p-4 pl-5 flex-1">
      <div className="w-12 h-12 bg-[#6db025]/10 flex items-center justify-center group-hover:bg-[#6db025]/20 transition-colors rounded"><Icon size={20} className="text-[#6db025]" /></div>
      <span className="text-white font-bold uppercase text-sm tracking-widest flex-1">{label}</span>
      <ChevronRight size={18} className="text-white/20 group-hover:text-[#6db025] group-hover:translate-x-1 transition-all" />
    </div>
  </Link>
);

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiRef = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([reportsAPI.getDashboard(), alertsAPI.getAll({})]);
      setStats(statsRes.data);
      const now = new Date();
      const recent = (alertsRes.data || []).filter(a => a.alert_type === 'milestone' && (now - new Date(a.created_at)) < 24*60*60*1000);
      setMilestones(recent);
      if (!confettiRef.current && recent.length > 0) { setShowConfetti(true); confettiRef.current = true; setTimeout(() => setShowConfetti(false), 5000); }
    } catch (e) { console.error('Dashboard fetch failed:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSeed = async () => {
    setSeeding(true);
    try { await seedAPI.seed(); await fetchStats(); } catch (e) { console.error(e); }
    finally { setSeeding(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-[#6db025] font-heading tracking-widest uppercase">Preluare Date...</div></div>;

  return (
    <div className="space-y-8">
      {showConfetti && <ConfettiOverlay />}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Panou Control</h1>
          <p className="text-white/40 text-sm mt-1">Sumarul activității clubului tău</p>
        </div>
        <button onClick={handleSeed} disabled={seeding} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-white/50 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors rounded disabled:opacity-40">
          <RefreshCw size={14} className={seeding ? 'animate-spin' : ''} /> {seeding ? 'Se execută...' : 'Re-Seed Bază Date'}
        </button>
      </div>

      {milestones.length > 0 && (
        <div className="relative overflow-hidden border border-yellow-400/30 bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-yellow-500/10 p-6 rounded">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center animate-pulse"><Trophy size={24} className="text-yellow-400" /></div>
            <div><h2 className="font-heading text-lg font-bold text-yellow-400 uppercase">Realizări de Sărbătorit!</h2><p className="text-yellow-400/40 text-[10px] uppercase font-bold tracking-widest">Milestone în ultimele 24h</p></div>
          </div>
          <div className="flex flex-wrap gap-3">
            {milestones.map(m => (
              <div key={m.id} className="flex items-center gap-3 px-4 py-3 bg-black/30 border border-yellow-400/10 rounded-sm">
                <span className="font-heading text-2xl font-black text-yellow-400">{m.milestone_value || '?'}</span>
                <div><p className="text-white font-bold text-xs uppercase">{m.person_name}</p><p className="text-yellow-400/40 text-[9px] uppercase">prezențe</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Utilizatori" value={stats?.total_users || 0} />
        <StatCard icon={CreditCard} label="Abonamente Active" value={stats?.active_subscriptions || 0} />
        <StatCard icon={TrendingUp} label="Venituri Luna Asta" value={`${stats?.monthly_revenue || 0} LEI`} />
        <StatCard icon={CalendarCheck} label="Prezențe Luna Asta" value={stats?.monthly_attendance || 0} />
      </div>

      {stats?.unseen_alerts > 0 && (
        <Link to="/admin/alerte" className="flex items-center gap-4 p-4 bg-yellow-500/10 border border-yellow-500/20 hover:border-yellow-500/40 transition-colors group rounded">
          <div className="bg-yellow-500/20 p-2 rounded-sm"><Bell className="text-yellow-500" size={20} /></div>
          <div className="flex-1">
            <p className="text-yellow-500 font-bold text-sm uppercase tracking-wider">{stats.unseen_alerts} Notificări de Sistem</p>
            <p className="text-white/40 text-[10px] uppercase font-black tracking-widest">Acțiuni necesare</p>
          </div>
          <ChevronRight className="text-yellow-500/40 group-hover:text-yellow-500 transition-all" size={20} />
        </Link>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-3">
          <h2 className="text-white font-black uppercase tracking-widest text-base sm:text-lg">Acțiuni Rapide</h2>
          <div className="space-y-2">
            <QuickAction icon={Users} label="Adaugă Utilizator" to="/admin/utilizatori" />
            <QuickAction icon={CalendarCheck} label="Înregistrare Intrare Bazin" to="/admin/prezenta" />
            <QuickAction icon={Bell} label="Verificare Alerte" to="/admin/alerte" />
            <QuickAction icon={TrendingUp} label="Analiză Financiară" to="/admin/rapoarte" />
          </div>
        </div>
        <div className="space-y-3">
          <h2 className="text-white font-black uppercase tracking-widest text-base sm:text-lg">Ultimele Accesări Bazin</h2>
          {stats?.recent_attendance?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_attendance.map((att, i) => (
                <div key={i} className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />
                  <div className="p-3 pl-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#6db025]/10 flex items-center justify-center rounded"><CalendarCheck size={18} className="text-[#6db025]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-bold uppercase truncate">{att.person_name}</p>
                      <p className="text-[#6db025]/70 text-[11px] uppercase font-black tracking-widest mt-0.5 truncate">{att.location}</p>
                    </div>
                    <span className="text-white/40 text-[11px] font-bold uppercase tracking-wider bg-white/5 px-2 py-1 rounded whitespace-nowrap">{new Date(att.recorded_at).toLocaleDateString('ro-RO')}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 p-12 text-center text-white/20 text-xs font-bold uppercase tracking-widest rounded">Nicio activitate recentă</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
